import React from 'react'
import { ThemeColors } from '@contrast-security-inc/design-system-foundations'
import { theme as deprecatedTheme } from '../../styled/theme'
import {
  Thermometer,
  ThermometerValueRendererProps,
  SegmentedThermometer
} from '../../legacy-design-system/thermometer'
import { GradeValue } from '../../graphql/schema/types/grade-value'
import { css } from '@emotion/css'
import { Tooltip } from '@contrast-security-inc/design-system-popovers'

interface GradeThermometerProps {
  grade: GradeValue
  ratio: number
  dataTestId?: string
  e2eTestId?: string
}

interface GradeThermometerSegmentProps {
  grade: GradeValue
  value: number
  width: string
  className: string
  label: string
}

export function getGradeColorKey(grade: GradeValue): ThemeColors {
  switch (grade) {
    case 'A':
      return 'green50'
    case 'B':
      return 'turquoise70'
    case 'C':
      return 'yellow50'
    case 'D':
      return 'orange50'
    case 'F':
      return 'red50'
    default:
      return 'neutral40'
  }
}

export function GradeThermometer(props: GradeThermometerProps) {
  return (
    <Thermometer
      dataTestId={props.dataTestId}
      e2eTestId={props.e2eTestId}
      fillColor={getGradeColorKey(props.grade)}
      fillRatio={props.ratio}
      renderValue={GradeThermometerValue}
    />
  )
}

function GradeThermometerValue(props: ThermometerValueRendererProps) {
  const { className } = props
  return <div className={className} />
}

function GradeThermometerEmptyState({
  e2eTestId
}: {
  readonly e2eTestId?: string
}) {
  return (
    <Thermometer fillRatio={100} fillColor="neutral30" e2eTestId={e2eTestId} />
  )
}

interface SegmentData {
  grade: GradeValue
  value: number
  label: string
}

interface GradeSegmentedThermometerProps {
  data: SegmentData[]
  e2eTestId?: string
}

export const processData: (data: SegmentData[]) => {
  sum: number
  grades: GradeValue[]
  values: number[]
} = (data) =>
  data.reduce(
    (prev, curr) =>
      curr.value === 0
        ? prev
        : {
            sum: prev.sum + curr.value,
            grades: [...prev.grades, curr.grade],
            values: [...prev.values, curr.value]
          },
    {
      sum: 0,
      grades: [],
      values: []
    }
  )

export function GradeSegmentedThermometer({
  data,
  e2eTestId
}: GradeSegmentedThermometerProps) {
  const { sum, grades, values } = processData(data)

  if (sum === 0) {
    return <GradeThermometerEmptyState e2eTestId={e2eTestId} />
  }

  const getColor = (i: number): ThemeColors => getGradeColorKey(grades[i])

  return (
    <SegmentedThermometer
      testId={e2eTestId}
      getColor={getColor}
      values={values.filter((value) => value > 0)}
      renderValue={({ index, width, propsGetter }) => {
        return (
          <GradeThermometerSegment
            grade={grades[index]}
            value={values[index]}
            width={width}
            label={data[index].label}
            {...propsGetter()}
          />
        )
      }}
    />
  )
}

function GradeThermometerSegment({
  width,
  grade,
  value,
  label,
  className
}: GradeThermometerSegmentProps) {
  return (
    <Tooltip
      isDelayed
      openDelay={150}
      closeDelay={150}
      body={
        <div>
          <span
            className={css`
              font-weight: ${deprecatedTheme.fontWeights.primary.bold};
            `}
          >
            {grade}
          </span>
          {`: ${value} ${label}`}
        </div>
      }
    >
      <div className={className} style={{ width }} />
    </Tooltip>
  )
}
